var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { ui, layout } from '@joint/plus';
import { StencilNode, StencilLink } from './shapes';
export function createStencil(paper, width, list, onNodeDrop) {
    var stencil = new ui.Stencil({
        paper: paper,
        width: width,
        dropAnimation: true,
        usePaperGrid: true,
        contentOptions: {
            useModelGeometry: true
        },
        paperOptions: {
            async: true,
            autoFreeze: true,
            frozen: true,
            viewManagement: {
                disposeHidden: true,
            },
            cellVisibility: function (cell) {
                // Never hide elements matched by the search
                if (cell.isElement() && cell.isMatched())
                    return true;
                if (cell.isLink() && cell.graph.get('grid'))
                    return false;
                // Hide elements and links which are currently collapsed
                if (cell.isHidden())
                    return false;
                return true;
            }
        },
        search: function (cell, keyword) {
            if (keyword === '')
                return true;
            if (cell.isLink())
                return false;
            // Do not show directories in the search results
            if (cell.isDirectory())
                return false;
            var path = cell.getPathString().toLowerCase();
            return path.includes(keyword.toLowerCase());
        },
        // Can be a directory dragged & dropped?
        canDrag: function (nodeView) {
            var node = nodeView.model;
            return !node.isDirectory();
        },
        // Replace the stencil shape with an actual shape while dragging
        dragStartClone: function (node) { return onNodeDrop(node); }
    });
    stencil.render();
    var stencilPaper = stencil.getPaper();
    var stencilGraph = stencil.getGraph();
    var stencilCells = buildCellsFromList(list);
    stencilGraph.resetCells(stencilCells);
    var stencilRoot = stencilGraph.getSources()[0];
    var stencilTree = new layout.TreeLayout({
        graph: stencilGraph,
        direction: 'BR',
        parentGap: -20,
        siblingGap: 5,
        firstChildGap: 5,
        updateVertices: null,
        filter: function (siblings, parent) {
            // Layout will skip elements which have been collapsed
            if (!parent || !parent.isCollapsed())
                return siblings;
            return [];
        },
        updateAttributes: function (_, node) {
            // silent change of hidden attribute (there is no need to trigger an event)
            node.attributes.hidden = false;
            // Update some presentation attributes during the layout
            node.toggleButtonVisibility(!stencilGraph.isSink(node));
            node.toggleButtonSign(!node.isCollapsed());
        }
    });
    function layoutTree() {
        // Reset tree layout start position
        stencilRoot.position(30, 10);
        // Or to Hide the first root level use this.
        // stencilRoot.position(10, -20);
        stencilGraph.getElements().forEach(function (el) { return el.attributes.hidden = true; });
        stencilTree.layout();
        stencilPaper.fitToContent({
            minWidth: width,
            contentArea: stencilTree.getLayoutBBox(),
            padding: { bottom: 20 }
        });
        stencilPaper.wakeUp();
    }
    function resetTree() {
        stencilGraph.set('grid', false);
        // Set name value as label and reset text annotations
        stencilGraph.getElements().forEach(function (node) { return node.unmatch(); });
        layoutTree();
    }
    function layoutGrid(matchedGraph) {
        // Display grid layout when graph is filtered
        layout.GridLayout.layout(matchedGraph, {
            verticalAlign: 'top',
            horizontalAlign: 'left',
            columns: 1,
            columnWidth: 'auto',
            rowGap: 6,
            marginX: 10,
            marginY: 10
        });
    }
    function resetGrid(matchedGraph, keyword) {
        stencilGraph.set('grid', true);
        stencilGraph.getElements().forEach(function (node) {
            if (matchedGraph.getCell(node.id)) {
                node.match(keyword);
            }
            else {
                node.unmatch();
            }
        });
        layoutGrid(matchedGraph);
    }
    function toggleBranch(root) {
        var shouldHide = !root.isCollapsed();
        root.set({ collapsed: shouldHide });
        layoutTree();
    }
    // Events
    stencil.on('filter', function (matchedGraph, _group, keyword) {
        if (keyword === '') {
            resetTree();
        }
        else {
            resetGrid(matchedGraph, keyword);
        }
    });
    stencilPaper.on('element:pointerdown', function (view) {
        var node = view.model;
        if (node.isDirectory()) {
            toggleBranch(node);
        }
    });
    resetTree();
    return stencil;
}
var DIRECTORY_ICON = 'assets/directory.svg';
var FILE_ICON = 'assets/file.svg';
function buildCellsFromList(list) {
    var elements = [];
    var links = [];
    var iterate = function (obj, pathArray) {
        if (pathArray === void 0) { pathArray = []; }
        var _a = obj.name, name = _a === void 0 ? '' : _a, _b = obj.icon, icon = _b === void 0 ? null : _b, _c = obj.children, children = _c === void 0 ? [] : _c, _d = obj.dir, dir = _d === void 0 ? false : _d, _e = obj.collapsed, collapsed = _e === void 0 ? false : _e;
        // Path displayed during search
        var path = pathArray.concat(name);
        var node = new StencilNode({ path: path, name: name, dir: dir, collapsed: collapsed });
        var defaultIcon = dir ? DIRECTORY_ICON : FILE_ICON;
        node.setIcon(icon || defaultIcon);
        node.attr(['root', 'dataDirectory'], dir);
        elements.push(node);
        children.forEach(function (child) {
            var childNode = iterate(child, path);
            var link = new StencilLink();
            link.connect(node.id, childNode.id);
            links.push(link);
        });
        return node;
    };
    iterate(list);
    return __spreadArray(__spreadArray([], elements, true), links, true);
}
